import Sentry from "@similarweb/sentry-logger";

import type { CrmIntegrationProvider } from "app/pages/sales-intelligence/domain/crm-integration/types/provider";
import {
    initAccountsExport,
    resetAccountsExport,
    setAccountsExportPhase,
} from "app/pages/sales-intelligence/modules/crm-export/store/action-creators";
import { fetchAccountsPreviewThunk } from "app/pages/sales-intelligence/modules/crm-export/store/effects";
import { getDomainService } from "app/pages/sales-intelligence/modules/crm-export/utils/getDomainService";
import { selectActiveLeadsType, selectIsAggregatedDataUsed } from "app/pages/sales-intelligence/modules/leads-search";
import { createAddTopNLeadsToListThunk } from "app/pages/sales-intelligence/modules/leads-search/store/effects";
import type { LeadsSearchRequestDto, LeadsType } from "app/pages/sales-intelligence/modules/leads-search/types";
import {
    contactsQueryKeys,
} from "app/pages/sales-intelligence/sub-modules/contacts-search/query/queryKeys/queryKeyFactory";
import { makeFetchTopNContactsThunk } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/effects";
import {
    createContactsTopNSearchResultsDto,
} from "app/pages/sales-intelligence/sub-modules/contacts-search/store/selectCombiners";
import {
    selectContactSearchResultsDto,
} from "app/pages/sales-intelligence/sub-modules/contacts-search/store/selectors";
import type { ContactsSearchResponseDto } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/dto";
import { exportToCrmToastThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/common";
import type { ThunkDependencies } from "single-spa/store/thunk-dependencies";
import type { ThunkDispatchCommon, ThunkGetState } from "single-spa/store/types";

import { fetchTopNLeadsDomains } from "../query/queryFn/fetchTopNLeadsDomains";


export const addTopNLeadsToListThunk = createAddTopNLeadsToListThunk(fetchTopNLeadsDomains);

const createFetchTopNContactsThunk = (topN: number) => {
    return async (
        _dispatch: ThunkDispatchCommon,
        getState: ThunkGetState,
        deps: ThunkDependencies,
    ): Promise<ContactsSearchResponseDto> => {
        const state = getState();

        const dto = selectContactSearchResultsDto(state);
        const activeLeadsType = selectActiveLeadsType(state);
        const isAggregatedDataUsed = selectIsAggregatedDataUsed(state);

        const topNDto = createContactsTopNSearchResultsDto(dto, topN, isAggregatedDataUsed);

        const response = await deps.si.queryClient.fetchQuery({
            queryKey: contactsQueryKeys.tableData(activeLeadsType, topNDto),
            queryFn({ signal }) {
                return deps.si.api.contactsSearch.fetchSearchResults(activeLeadsType, topNDto, {
                    cancellation: signal,
                });
            },
        });

        return response;
    };
};

export const fetchTopNContactsThunk = makeFetchTopNContactsThunk(createFetchTopNContactsThunk);

type StartAccountsCrmExportParams = {
    integration: CrmIntegrationProvider;
    leadsType: LeadsType;
    countries: readonly number[];
    domains?: readonly string[];
    domainsCount?: number;
    dto?: Readonly<LeadsSearchRequestDto>;
};

/**
 * Starts the two-step CRM account export flow for companies/websites.
 * Resolves the domains to export (either the manually selected ones or the top-N results),
 * fetches the CRM match preview, and always opens the "Review & Export" modal.
 */
export const startLeadsAccountsCrmExportThunk =
    (params: StartAccountsCrmExportParams) =>
    async (dispatch: ThunkDispatchCommon): Promise<void> => {
        const { integration, leadsType, countries, domains, domainsCount, dto } = params;

        dispatch(initAccountsExport({ integration, leadsType, countries }));
        dispatch(setAccountsExportPhase({ type: "matchingAccounts", integration }));

        try {
            const resolvedDomains = domains ?? (await fetchTopNLeadsDomains({ leadsType, domainsCount, dto })) ?? [];

            await dispatch(fetchAccountsPreviewThunk(integration, leadsType, resolvedDomains));

            dispatch(setAccountsExportPhase({ type: "previewingAccounts", integration }));
        } catch (error) {
            // User cancelled the preview (loading modal Cancel) — the request was aborted, no error UX needed.
            if ((error as Error)?.name === "AbortError") {
                return;
            }

            Sentry.errorTracker({
                message: "Failed to start leads accounts CRM export",
                e: error,
                level: "error",
            });
            dispatch(exportToCrmToastThunk(getDomainService(integration).getErrorToastTitle()));
            dispatch(resetAccountsExport());
        }
    };
